import { useMemo } from "react";
import { useGetAllLocations } from "@/locations/api/queries/location.queries.ts";
import type { TLocation } from "@/locations/types/location.ts";
import type { Machine } from "@/machines/types/machine.ts";
import { useMachineKpis, type MachineKpi } from "./useMachineKpis";

export interface MapPageData {
  locations: TLocation[];
  selectedLocation: TLocation | null;
  machines: Machine[];
  selectedMachine: Machine | null;
  /** Per-machine KPIs keyed by `device_mxid`. */
  kpisByDevice: Record<string, MachineKpi>;
  isLoadingLocations: boolean;
  isLoadingMachines: boolean;
  isError: boolean;
}

/**
 * Single orchestration hook for the `/map` page.
 *
 * Machines (incl. `last_gps`, `status`) come from the location list response
 * (`/users/locations/`) — NOT `/api/machines/?location_id=`, which omits GPS and
 * status. This matches what location-detail renders on its map. Org + auth
 * headers are injected by the axios interceptor; the date range comes from the
 * global `useDateTimeRangeStore` (via `useMachineKpis`).
 */
/** Machine pins render `last_gps` from the location list — repoll it every
 * minute while the map is open so they track the machines' latest positions. */
const MACHINE_GPS_REFRESH_MS = 60_000;

export const useMapPageData = (
  selectedLocationId: number | null,
  selectedMachineId: number | null,
): MapPageData => {
  const locationsQuery = useGetAllLocations({ refetchInterval: MACHINE_GPS_REFRESH_MS });

  const locations = useMemo(() => locationsQuery.data?.data ?? [], [locationsQuery.data]);

  const selectedLocation = useMemo(
    () => locations.find((l) => l.id === selectedLocationId) ?? null,
    [locations, selectedLocationId],
  );

  // The selected location already carries its machines (with GPS) from the list.
  const machines = useMemo(() => selectedLocation?.machines ?? [], [selectedLocation]);

  const selectedMachine = useMemo(
    () => machines.find((m) => m.id === selectedMachineId) ?? null,
    [machines, selectedMachineId],
  );

  const kpisByDevice = useMachineKpis(machines);

  return {
    locations,
    selectedLocation,
    machines,
    selectedMachine,
    kpisByDevice,
    isLoadingLocations: locationsQuery.isPending,
    isLoadingMachines: locationsQuery.isPending,
    isError: locationsQuery.isError,
  };
};
